
import type { TripsAnalyticItem } from '@/new-services/types/tms'

import { defineComponent, reactive, onMounted } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// module components
import TripsAnalyticsFilters from '@/modules/TripsAnalyticsModule/components/TripsAnalyticsFilters/index.vue'
import TripsAnalyticsItem from '@/modules/TripsAnalyticsModule/components/TripsAnalyticsItem/index.vue'

// types
import { GetTripsAnalyticsParams } from '@/new-services/types/tms'

// new-services
import tmsAPI from '@/new-services/tms.service'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useFile } from '@/common/composable/useFile'
import { useGlobalStore } from '@/store/globalStore'

export default defineComponent({
    components: {
        PageTitle,
        TripsAnalyticsFilters,
        TripsAnalyticsItem,
        Loader,
        NewButton,
    },
    setup() {
        const notifications = useNotifications()
        const { saveFile } = useFile()
        const store = useGlobalStore()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as Array<TripsAnalyticItem>,
            is_export_loading: false,
        })

        const params_state = reactive({
            departureDateFrom: null as string | null,
            departureDateTo: null as string | null,
            arrivalDateFrom: null as string | null,
            arrivalDateTo: null as string | null,
            //
            tripNumber: null as string | null | undefined,
            fromCityId: null as number | null | undefined,
            toCityId: null as number | null | undefined,
            utilizationIds: [] as Array<number>,
            logistId: null as number | null | undefined,
        })

        const loadTripsAnalytics = async () => {
            try {
                state.is_loading = true

                const response = await tmsAPI.getTripsAnalytics({
                    departureDateFrom: params_state.departureDateFrom as string,
                    departureDateTo: params_state.departureDateTo as string,
                    arrivalDateFrom: params_state.arrivalDateFrom as string,
                    arrivalDateTo: params_state.arrivalDateTo as string,
                    tripNumber: params_state.tripNumber,
                    fromCityId: params_state.fromCityId,
                    toCityId: params_state.toCityId,
                    utilizationIds: params_state.utilizationIds,
                    logistId: params_state.logistId,
                })

                state.list = response.data.data

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const onFiltersSubmit = async (params: GetTripsAnalyticsParams) => {
            params_state.departureDateFrom = params.departureDateFrom
            params_state.departureDateTo = params.departureDateTo
            params_state.arrivalDateFrom = params.arrivalDateFrom
            params_state.arrivalDateTo = params.arrivalDateTo
            params_state.tripNumber = params.tripNumber
            params_state.fromCityId = params.fromCityId
            params_state.toCityId = params.toCityId
            params_state.utilizationIds = params.utilizationIds
            params_state.logistId = params.logistId

            loadTripsAnalytics()
        }

        const exportTripsAnalytics = async () => {
            if (state.is_export_loading) return

            try {
                state.is_export_loading = true

                const response = await tmsAPI.exportTripsAnalytics({
                    departureDateFrom: params_state.departureDateFrom as string,
                    departureDateTo: params_state.departureDateTo as string,
                    arrivalDateFrom: params_state.arrivalDateFrom as string,
                    arrivalDateTo: params_state.arrivalDateTo as string,
                    tripNumber: params_state.tripNumber,
                    fromCityId: params_state.fromCityId,
                    toCityId: params_state.toCityId,
                    utilizationIds: params_state.utilizationIds,
                    logistId: params_state.logistId,
                })

                saveFile(response.data, `Аналитика рейсов`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При экспорте данных произошла ошибка' })
            } finally {
                state.is_export_loading = false
            }
        }

        onMounted(() => {
            store.closeSidebar()
        })

        return {
            state,
            onFiltersSubmit,
            loadTripsAnalytics,
            exportTripsAnalytics,
        }
    },
})
